import { useEffect, useRef, useState } from 'react'

/**
 * Arrow-key navigation for a custom listbox / combobox popover (the
 * searchable country dropdowns in PhoneInputField / CountryFilter /
 * IpAccessSettings all share this shape).
 *
 * Returns:
 *   - `highlightedIndex`   — the currently-highlighted row.
 *   - `setHighlightedIndex`— call from the trigger's open handler (to seed
 *                            the highlight) and the search `onChange` (to
 *                            reset it as the list narrows). Kept in the
 *                            component's event handlers — NOT an effect —
 *                            so it never triggers a cascading-render.
 *   - `optionRefs`         — attach `ref={(el) => (optionRefs.current[i] = el)}`
 *                            to each row so the highlighted one scrolls into
 *                            view during navigation.
 *   - `onKeyDown`          — bind to the search input. ↓/↑ move the highlight
 *                            (clamped); Enter calls `onEnter(highlightedIndex)`.
 *
 * @param {object}   opts
 * @param {boolean}  opts.open    Whether the popover is open.
 * @param {number}   opts.count   Number of navigable rows.
 * @param {(index:number)=>void} opts.onEnter Select the highlighted row.
 */
export function useListboxNav({ open, count, onEnter }) {
  const [highlightedIndex, setHighlightedIndex] = useState(0)
  const optionRefs = useRef([])

  // Keep the highlighted row scrolled into view as the user arrows. Only a
  // DOM read/scroll — no setState — so it can't cascade renders.
  useEffect(() => {
    if (!open) return
    optionRefs.current[highlightedIndex]?.scrollIntoView({ block: 'nearest' })
  }, [highlightedIndex, open])

  function onKeyDown(e) {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.min(i + 1, count - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      onEnter(highlightedIndex)
    }
  }

  return { highlightedIndex, setHighlightedIndex, optionRefs, onKeyDown }
}
